import type { ReduxAction } from '@repo/utils/types';
import AppState from '../app-state';
import Occasion from '@repo/utils/helpers/Occasion';
import BaseObject from '@repo/utils/models/BaseObject';

export type AuraFreshnessInterval = 'monthly' | 'quarterly' | 'biannually' | 'annually';

export interface AuraOptions {
  is_enabled: boolean;
  is_limited: boolean;
  interval: AuraFreshnessInterval;
  dollarLimit: number | string;
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  // @ts-ignore
  trailingZeroDisplay: 'stripIfInteger',
});

export class UserAuraSettings extends BaseObject {
  is_enabled: boolean;
  is_limited: boolean;
  is_delinquent: boolean;
  end_date: Occasion;
  start_date: Occasion;
  limit: number | string;
  spend: number;
  interval: AuraFreshnessInterval;
  count: number;
  enabling: boolean;
  dollarLimit: number | string;
  dollarSpend: number | string;

  constructor(data: Partial<UserAuraSettings> = {}) {
    super(data);

    this.is_enabled = data.is_enabled || false;
    this.is_limited = data.is_limited || false;
    this.is_delinquent = data.is_delinquent || false;
    this.count = data.count || 0;
    this.interval = data.interval || 'monthly';
    this.end_date = new Occasion(data.end_date);
    this.start_date = new Occasion(data.start_date);
    this.enabling = data.enabling || false;
    this.dollarLimit = data.dollarLimit;
    this.limit = data.limit || 0;

    if (data.dollarLimit === undefined) {
      this.dollarLimit = Number(data.limit) / 100;
      this.limit = Number(data.limit || 0);
    }

    this.dollarSpend = '$0';

    if (data.dollarSpend && data.dollarSpend[0] === '$') {
      this.dollarSpend = data.dollarSpend;
    } else if (data.spend !== undefined) {
      this.dollarSpend = formatter.format(Number(data.spend) / 100);
    }
  }
}

const initialAuraState = new UserAuraSettings();

export const selectAuraSettings = (state: AppState) => state.user.auraSettings;

export const selectAuraUserOptions = (state: AppState) => {
  const auraSettings = selectAuraSettings(state);
  return {
    is_enabled: auraSettings.is_enabled,
    is_limited: auraSettings.is_limited,
    limit: Number(auraSettings.dollarLimit || 0) * 100,
    interval: auraSettings.interval,
    enabling: auraSettings.enabling,
  };
};

export function userAuraSettingsReducer(
  state: UserAuraSettings = initialAuraState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'FETCH_AURA_SETTINGS_SUCCEEDED':
      return new UserAuraSettings({ ...payload.response, enabling: false });

    case 'UPDATE_AURA_SETTINGS':
      if (payload.is_enabled) {
        return state.copyWith({ ...payload, enabling: true });
      }

      return state.copyWith(payload);

    case 'UPGRADE_PLAN_AND_ENABLE_AURA':
      return state.copyWith({ is_enabled: true, enabling: true });

    case 'UPDATE_AURA_SETTINGS_FAILED':
      // if we were trying to enable, show an error
      if (state.enabling) {
        return state.copyWith({ enabling: false, is_enabled: false, error: true });
      }

    default:
      return state;
  }
}
